import clsx from "clsx";
import Status from "components/statusDisplay/Status";

import CompanyLogo from "~/components/CompanyLogo";
import Headline from "~/components/Headline/Headline.tsx";
import Hint from "~/components/Hint";
import { useAllCompanyAvatarImages } from "~/modules/image/api/avatarImage/avatarImageQueries.ts";
import type { UsersActiveStaffingType } from "~/modules/project/api/staffing/staffingTypes.ts";

type ProjectsProps = {
	usersActiveStaffings: UsersActiveStaffingType[];
	trackedStaffingIds: string[];
	staffingIdsOutsidePeriod: string[];
	value: string | null;
	onChange: (staffingId: string) => void;
};

export function Projects({ usersActiveStaffings, trackedStaffingIds, staffingIdsOutsidePeriod, onChange }: ProjectsProps) {
	const { data: allCompanyAvatarImages } = useAllCompanyAvatarImages();

	return (
		<div className="grid gap-8">
			{usersActiveStaffings.length > 0 ? (
				usersActiveStaffings.map(({ id, projectPhases, title, projectNumber, clientId }) => {
					const avatarImage = allCompanyAvatarImages?.find((image) => image.clientIds.includes(clientId));
					return <div key={`project-${id}`}
								className="flex flex-col gap-1">
						<div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
							{avatarImage && <CompanyLogo image={avatarImage} sizeClassName="size-6"/> }
							<Headline type="h4">
								{title}
							</Headline>
							<div className="ml-auto">
								<Status>{projectNumber}</Status>
							</div>
						</div>
						<div className="flex flex-col flex-wrap justify-start gap-y-2 rounded-lg bg-gray-100 p-2">
							{projectPhases.map((phase) => {
								return phase.orders.map((order) => {
									const staffingId = order.staffings[0].id;
									const isAlreadyTracked = trackedStaffingIds.includes(staffingId);
									const isOutsidePeriod = staffingIdsOutsidePeriod.includes(staffingId);
									return (
										<button key={`order-${order.id}`}
												className={clsx("group divide-y divide-gray-100 rounded-md bg-white px-2 text-primary-500 hover:bg-gray-50", isOutsidePeriod && "pointer-events-none opacity-50")}
												onClick={() => onChange(staffingId)}
										>
											<div className="grid gap-1 py-2 text-left sm:grid-cols-6 sm:gap-4">
												<dt className="text-sm text-gray-500 sm:text-right">Phase</dt>
												<dd className="text-sm font-bold sm:col-span-5">{phase.title}</dd>
											</div>
											<div className="grid gap-1 py-2 text-left sm:grid-cols-6 sm:gap-4">
												<dt className="text-sm text-gray-500 sm:text-right">
													Bestellung
												</dt>
												<dd className="text-sm font-bold sm:col-span-5">
													<div
														className="flex flex-row gap-1"
													>
														{order.title}
													</div>
												</dd>
											</div>
											{isOutsidePeriod &&
												<div className="rounded bg-accent-500 py-1 text-sm">Der Tag liegt
													außerhalb des Staffing-Zeitraums.</div>}
											{isAlreadyTracked && !isOutsidePeriod &&
												<div className="py-2 text-left">
													<Hint size="sm">Für diese Bestellung gibt es an diesem Tag bereits
														eine oder mehrere Zeiterfassungen.</Hint>
												</div>}
										</button>
									);
								});
							})}
						</div>
					</div>;
				})
			) : (
				<Hint size="sm">Du hast derzeit keine aktiven Staffings / Bestellungen.</Hint>
			)}
		</div>
	);
}
